package com.daytimegaming.ourdailyfinances.presentation.budget

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.InputChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import com.daytimegaming.ourdailyfinances.domain.model.Dashboard
import com.daytimegaming.ourdailyfinances.presentation.components.CategoriesBottomSheet
import com.daytimegaming.ourdailyfinances.presentation.preview.CreateBudgetPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.defaultColors
import com.daytimegaming.ourdailyfinances.presentation.util.CATEGORIES
import org.koin.compose.viewmodel.koinViewModel
import kotlin.random.Random

private fun cleanCategoryName(primary: String, detailed: String): String {
    val cleanPrimary = primary.trim()
    val cleanDetailed = detailed.trim()
    val remaining = if (cleanPrimary.isNotEmpty() && cleanDetailed.startsWith(cleanPrimary)) {
        val suffix = cleanDetailed.removePrefix(cleanPrimary).removePrefix("_")
        suffix.ifEmpty { cleanPrimary }
    } else {
        cleanDetailed.ifEmpty { "General" }
    }

    return remaining.split("_", " ")
        .filter { it.isNotEmpty() }
        .joinToString(" ") { word ->
            word.lowercase().replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
        }
}

class CurrencyVisualTransformation(val prefix: String = "$") : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        val cleanText = text.text.filter { it.isDigit() }
        if (cleanText.isEmpty()) {
            val emptyTransformed = prefix + "0.00"
            return TransformedText(
                AnnotatedString(emptyTransformed),
                object : OffsetMapping {
                    override fun originalToTransformed(offset: Int): Int = emptyTransformed.length
                    override fun transformedToOriginal(offset: Int): Int = 0
                }
            )
        }

        val parsed = cleanText.toLongOrNull() ?: 0L
        val wholePart = parsed / 100
        val decimalPart = parsed % 100

        val wholePartStr = formatWholePart(wholePart)
        val decimalPartStr = decimalPart.toString().padStart(2, '0')

        val transformedNumber = "$wholePartStr.$decimalPartStr"
        val formatted = prefix + transformedNumber

        val offsetMapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                val originalOffset = offset.coerceIn(0, cleanText.length)
                var digitCount = 0
                var transformedOffset = prefix.length
                while (digitCount < originalOffset && transformedOffset < formatted.length) {
                    if (formatted[transformedOffset].isDigit()) {
                        digitCount++
                    }
                    transformedOffset++
                }
                return transformedOffset
            }

            override fun transformedToOriginal(offset: Int): Int {
                val transformedOffset = offset.coerceIn(0, formatted.length)
                var digitCount = 0
                for (i in prefix.length until transformedOffset) {
                    if (i < formatted.length && formatted[i].isDigit()) {
                        digitCount++
                    }
                }
                return digitCount.coerceIn(0, cleanText.length)
            }
        }

        return TransformedText(AnnotatedString(formatted), offsetMapping)
    }

    private fun formatWholePart(value: Long): String {
        val valueStr = value.toString()
        val result = StringBuilder()
        var count = 0
        for (i in valueStr.length - 1 downTo 0) {
            if (count > 0 && count % 3 == 0) {
                result.append(',')
            }
            result.append(valueStr[i])
            count++
        }
        return result.reverse().toString()
    }
}

@Composable
fun CreateBudgetScreen(
    onNavigateBack: () -> Unit,
    viewModel: CreateBudgetViewModel = koinViewModel(key = remember { Random.nextInt().toString() })
) {
    val state by viewModel.state.collectAsState()

    CreateBudgetScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onCreateBudget = { viewModel.createBudget(onNavigateBack) },
        onNameChange = { viewModel.onNameChange(it) },
        onTargetValueChange = { viewModel.onTargetValueChange(it) },
        onToggleCategory = { viewModel.toggleCategory(it) },
        onToggleDashboard = { viewModel.toggleDashboard(it) },
        onClearError = { viewModel.clearError() }
    )
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun CreateBudgetScreenContent(
    state: CreateBudgetState,
    onNavigateBack: () -> Unit,
    onCreateBudget: () -> Unit,
    onNameChange: (String) -> Unit,
    onTargetValueChange: (String) -> Unit,
    onToggleCategory: (String) -> Unit,
    onToggleDashboard: (Dashboard) -> Unit,
    onClearError: () -> Unit,
) {
    var showBottomSheet by remember { mutableStateOf(false) }
    val sheetState = rememberModalBottomSheetState()
    var showCategoriesBottomSheet by remember { mutableStateOf(false) }
    val categoriesSheetState = rememberModalBottomSheetState()
    var showDiscardConfirmationDialog by remember { mutableStateOf(false) }

    val hasChanges = state.name.isNotEmpty() || state.targetValue.isNotEmpty() ||
            state.selectedCategories.isNotEmpty() || state.selectedDashboards.isNotEmpty()

    val navState = rememberNavigationEventState(NavigationEventInfo.None)

    NavigationBackHandler(
        state = navState,
        isBackEnabled = hasChanges,
        onBackCompleted = {
            showDiscardConfirmationDialog = true
        }
    )

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Create Budget") },
                navigationIcon = {
                    IconButton(
                        onClick = {
                            if (hasChanges) {
                                showDiscardConfirmationDialog = true
                            } else {
                                onNavigateBack()
                            }
                        }
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        },
        bottomBar = {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .navigationBarsPadding()
                    .imePadding()
                    .padding(horizontal = 16.dp, vertical = 8.dp)
            ) {
                if (state.isLoading) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                    }
                } else {
                    val targetDouble = state.targetValue.toDoubleOrNull()?.div(100.0) ?: 0.0
                    val isEnabled = state.name.isNotBlank() && state.selectedCategories.isNotEmpty() && targetDouble > 0.0
                    Button(
                        onClick = onCreateBudget,
                        enabled = isEnabled,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.primary,
                            contentColor = Color(0xFF003731),
                            disabledContainerColor = MaterialTheme.colorScheme.primary.copy(alpha = 0.5f),
                            disabledContentColor = Color(0xFF003731).copy(alpha = 0.5f)
                        ),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp)
                    ) {
                        Text(
                            text = "Create Budget",
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.titleMedium
                        )
                    }
                }
            }
        }
    ) { innerPadding ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .padding(horizontal = 16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            item {
                GlassCard(modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        Text(
                            text = "Budget Name",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(bottom = 8.dp),
                        )
                        OutlinedTextField(
                            value = state.name,
                            onValueChange = onNameChange,
                            placeholder = { Text("e.g. Groceries, Coffee, Utilities", color = MaterialTheme.colorScheme.surfaceVariant) },
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            colors = OutlinedTextFieldDefaults.defaultColors(),
                            modifier = Modifier.fillMaxWidth(),
                            keyboardOptions = KeyboardOptions(
                                capitalization = KeyboardCapitalization.Words,
                            )
                        )
                    }
                }
            }

            item {
                GlassCard(modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        Text(
                            text = "Target Spend Limit",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(bottom = 8.dp),
                        )
                        OutlinedTextField(
                            value = state.targetValue,
                            onValueChange = onTargetValueChange,
                            placeholder = { Text("$0.00", color = MaterialTheme.colorScheme.surfaceVariant) },
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            colors = OutlinedTextFieldDefaults.defaultColors(),
                            modifier = Modifier.fillMaxWidth(),
                            keyboardOptions = KeyboardOptions(
                                keyboardType = KeyboardType.Number,
                            ),
                            visualTransformation = CurrencyVisualTransformation()
                        )
                    }
                }
            }

            item {
                GlassCard(modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    text = "Budget Categories",
                                    style = MaterialTheme.typography.titleSmall,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                                Text(
                                    text = "Configure the target budget using one or more transaction categories. (At least 1 category must be selected)",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                            IconButton(onClick = { showCategoriesBottomSheet = true }) {
                                Icon(
                                    imageVector = Icons.Default.Add,
                                    contentDescription = "Add Categories",
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }
                        }

                        if (state.selectedCategories.isEmpty()) {
                            Text(
                                text = "No categories selected yet. (At least 1 category must be selected)",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.outline,
                                modifier = Modifier.padding(top = 8.dp)
                            )
                        } else {
                            Spacer(modifier = Modifier.height(8.dp))
                            FlowRow(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                state.selectedCategories.forEach { detailedCode ->
                                    val categoryPair = CATEGORIES.find { it.second == detailedCode }
                                    val primary = categoryPair?.first ?: ""
                                    val cleanName = cleanCategoryName(primary, detailedCode)
                                    InputChip(
                                        selected = true,
                                        onClick = { onToggleCategory(detailedCode) },
                                        label = { Text(cleanName) },
                                        trailingIcon = {
                                            Icon(
                                                imageVector = Icons.Default.Delete,
                                                contentDescription = "Remove",
                                                modifier = Modifier.size(16.dp)
                                            )
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
            }
            
            item {
                GlassCard(modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    text = "Link to Dashboards",
                                    style = MaterialTheme.typography.titleSmall,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                                Text(
                                    text = "Optionally link this budget to transactions associated to a dashboard. Private budgets only use transactions associated to your linked accounts.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                            IconButton(onClick = { showBottomSheet = true }) {
                                Icon(
                                    imageVector = Icons.Default.Add,
                                    contentDescription = "Add Dashboard",
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }
                        }

                        if (state.selectedDashboards.isEmpty()) {
                            Text(
                                text = "No dashboards linked. (Private budget)",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.outline,
                                modifier = Modifier.padding(top = 8.dp)
                            )
                        } else {
                            Spacer(modifier = Modifier.height(8.dp))
                            FlowRow(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                state.selectedDashboards.forEach { dashboard ->
                                    InputChip(
                                        selected = true,
                                        onClick = { onToggleDashboard(dashboard) },
                                        label = { Text(dashboard.name) },
                                        trailingIcon = {
                                            Icon(
                                                imageVector = Icons.Default.Delete,
                                                contentDescription = "Remove",
                                                modifier = Modifier.size(16.dp)
                                            )
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
            }

            state.error?.let { err ->
                item {
                    Card(
                        colors = CardDefaults.cardColors(
                            containerColor = MaterialTheme.colorScheme.errorContainer
                        ),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Row(
                            modifier = Modifier.padding(16.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = err,
                                color = MaterialTheme.colorScheme.onErrorContainer,
                                style = MaterialTheme.typography.bodyMedium,
                                modifier = Modifier.weight(1f)
                            )
                            IconButton(onClick = onClearError) {
                                Icon(
                                    imageVector = Icons.Default.Delete,
                                    contentDescription = "Dismiss error",
                                    tint = MaterialTheme.colorScheme.onErrorContainer
                                )
                            }
                        }
                    }
                }
            }
            
            item {
                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }

    if (showBottomSheet) {
        ModalBottomSheet(
            onDismissRequest = { showBottomSheet = false },
            sheetState = sheetState
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
                    .safeContentPadding()
            ) {
                Text(
                    text = "Select Dashboards",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 16.dp)
                )

                if (state.availableDashboards.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 32.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = "No dashboards available.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                } else {
                    LazyColumn(
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        items(state.availableDashboards, key = { it.dashboardId }) { dashboard ->
                            val isSelected = state.selectedDashboards.any { it.dashboardId == dashboard.dashboardId }
                            Card(
                                onClick = { onToggleDashboard(dashboard) },
                                colors = CardDefaults.cardColors(
                                    containerColor = if (isSelected) {
                                        MaterialTheme.colorScheme.primaryContainer
                                    } else {
                                        MaterialTheme.colorScheme.surfaceVariant
                                    }
                                ),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Row(
                                    modifier = Modifier.padding(16.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = dashboard.name,
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.SemiBold,
                                        color = if (isSelected) {
                                            MaterialTheme.colorScheme.onPrimaryContainer
                                        } else {
                                            MaterialTheme.colorScheme.onSurfaceVariant
                                        },
                                        modifier = Modifier.weight(1f)
                                    )
                                    Checkbox(
                                        checked = isSelected,
                                        onCheckedChange = { onToggleDashboard(dashboard) }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (showCategoriesBottomSheet) {
        CategoriesBottomSheet(
            selectedCategories = state.selectedCategories,
            onToggleCategory = onToggleCategory,
            onDismissRequest = { showCategoriesBottomSheet = false },
            sheetState = categoriesSheetState
        )
    }

    if (showDiscardConfirmationDialog) {
        AlertDialog(
            onDismissRequest = { showDiscardConfirmationDialog = false },
            title = { Text("Discard Changes") },
            text = { Text("You have unsaved changes. Are you sure you want to discard them?") },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDiscardConfirmationDialog = false
                        onNavigateBack()
                    }
                ) {
                    Text("Discard", color = MaterialTheme.colorScheme.error)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showDiscardConfirmationDialog = false }
                ) {
                    Text("Cancel")
                }
            }
        )
    }
}

@PreviewLightDark
@Composable
fun CreateBudgetScreenPreview(
    @PreviewParameter(CreateBudgetPreviewParameterProvider::class) state: CreateBudgetState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            CreateBudgetScreenContent(
                state = state,
                onNavigateBack = {},
                onCreateBudget = {},
                onNameChange = {},
                onTargetValueChange = {},
                onToggleCategory = {},
                onToggleDashboard = {},
                onClearError = {}
            )
        }
    }
}


